// src/pages/student-portal/courses/CoursePurchaseModal.js
// Student Portal - Course Purchase Modal (Purple Theme)

import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    CircularProgress,
    Alert,
    Radio,
    Chip,
    Divider,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import LayersIcon from '@mui/icons-material/Layers';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ShieldIcon from '@mui/icons-material/Shield';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripe_publishable_key } from '../../../config';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';
const ACCENT_DARK = '#7c3aed';
const ACCENT_LIGHT = '#a78bfa';

const stripePromise = loadStripe(stripe_publishable_key);

const CURRENCY_SYMBOLS = { usd: '$', eur: '€', gbp: '£', jpy: '¥', cad: 'CA$', aud: 'A$', inr: '₹', brl: 'R$', krw: '₩', mxn: 'MX$' };
function getCurrencySymbol(code) {
    if (!code) return '$';
    if (code.length <= 1 || CURRENCY_SYMBOLS[code.toLowerCase()]) return CURRENCY_SYMBOLS[(code || '').toLowerCase()] || '$';
    return code.toUpperCase() + ' ';
}

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#1e293b',
            fontFamily: "'Plus Jakarta Sans', 'Helvetica Neue', Helvetica, sans-serif",
            fontSmoothing: 'antialiased',
            fontSize: '15px',
            '::placeholder': { color: '#94a3b8' },
        },
        invalid: { color: '#ef4444', iconColor: '#ef4444' },
    },
};

// ──────────────────────────────────────────
// New Card Form (inside Stripe Elements)
// ──────────────────────────────────────────
const NewCardForm = memo(({ course, clientSecret, onSuccess, onError, isProcessingExternal }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [saveCard, setSaveCard] = useState(false);
    const [cardSaveStatus, setCardSaveStatus] = useState(''); // '', 'saving', 'saved', 'failed'
    const processing = isProcessing || isProcessingExternal;
    const effectivePrice = course?.effective_price ?? course?.price ?? 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || processing) return;
        onError('');
        setCardSaveStatus('');
        setIsProcessing(true);

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            onError('Card element not found.');
            setIsProcessing(false);
            return;
        }

        const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement },
        });

        if (paymentError) {
            onError(paymentError.message || 'Payment failed.');
            setIsProcessing(false);
            return;
        }

        if (paymentIntent?.status === 'succeeded') {
            // Save card if requested
            if (saveCard && paymentIntent.payment_method) {
                setCardSaveStatus('saving');
                try {
                    const saveRes = await axiosInstance.post('/payment-methods', {
                        gateway_payment_method_id: paymentIntent.payment_method,
                        set_as_default: false,
                    });
                    if (saveRes.data?.error) {
                        console.error('Card save error:', saveRes.data.error);
                        setCardSaveStatus('failed');
                    } else {
                        setCardSaveStatus('saved');
                    }
                } catch (err) {
                    console.error('Failed to save card:', err);
                    setCardSaveStatus('failed');
                }
            }
            onSuccess(paymentIntent.id);
        } else {
            onError(`Payment status: ${paymentIntent?.status}`);
        }
        setIsProcessing(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: '12px', bgcolor: 'white', mb: 2, position: 'relative' }}>
                {processing && (
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.8)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
                        <CircularProgress size={24} sx={{ color: ACCENT }} />
                    </Box>
                )}
                <CardElement options={CARD_ELEMENT_OPTIONS} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <FormControlLabel
                    control={<Checkbox checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} size="small" sx={{ color: '#cbd5e1', '&.Mui-checked': { color: ACCENT } }} />}
                    label={<Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>Save this card for future purchases</Typography>}
                />
                {cardSaveStatus === 'saved' && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#10b981', fontWeight: 600 }}>Card saved!</Typography>
                )}
                {cardSaveStatus === 'failed' && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#ef4444', fontWeight: 600 }}>Save failed</Typography>
                )}
            </Box>
            <Button fullWidth type="submit" variant="contained" disabled={!stripe || processing}
                sx={{ fontFamily, fontWeight: 700, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '12px', py: 1.5, fontSize: '0.9rem', boxShadow: `0 4px 16px ${ACCENT}44` }}>
                {processing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} sx={{ color: 'white' }} /> Processing...</Box>
                ) : (
                    `Pay ${getCurrencySymbol(course?.currency)}${Number(effectivePrice).toFixed(2)}`
                )}
            </Button>
        </form>
    );
});

// ──────────────────────────────────────────
// Main Purchase Modal
// ──────────────────────────────────────────
export default function CoursePurchaseModal({ open, onClose, course, onPurchaseSuccess }) {
    const [activeTab, setActiveTab] = useState('saved'); // 'saved' | 'new'
    const [methods, setMethods] = useState([]);
    const [selectedMethodId, setSelectedMethodId] = useState(null);
    const [isLoadingMethods, setIsLoadingMethods] = useState(true);
    const [clientSecret, setClientSecret] = useState(null);
    const [isLoadingIntent, setIsLoadingIntent] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');

    const effectivePrice = course?.effective_price ?? course?.price ?? 0;
    const totalLessons = course?.modules?.reduce((acc, m) => acc + (m.submodules?.reduce((a, s) => a + (s.content_items?.length || 0), 0) || 0), 0) || 0;

    // Fetch saved payment methods
    const fetchMethods = useCallback(async () => {
        setIsLoadingMethods(true);
        try {
            const res = await axiosInstance.get('/payment-methods');
            const list = Array.isArray(res.data) ? res.data : (res.data?.payment_methods || res.data?.data || []);
            setMethods(list);
            if (list.length > 0) {
                const def = list.find((m) => m.is_default);
                setSelectedMethodId(def?.id || list[0].id);
                setActiveTab('saved');
            } else {
                setActiveTab('new');
            }
        } catch (err) {
            console.error('Failed to fetch payment methods:', err);
            setActiveTab('new');
        } finally {
            setIsLoadingMethods(false);
        }
    }, []);

    // Create payment intent for new card flow
    const createPaymentIntent = useCallback(async () => {
        if (!course || isLoadingIntent || clientSecret) return;
        setIsLoadingIntent(true);
        setError('');
        try {
            const res = await axiosInstance.post('/api/lms/portal/courses/purchase-intent', {
                course_id_or_identity: course.course_identity || course.id,
            });
            if (res.data?.success) {
                setClientSecret(res.data.data?.client_secret);
            } else {
                setError(res.data?.message || 'Failed to initialize payment');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to initialize payment');
        } finally {
            setIsLoadingIntent(false);
        }
    }, [course, isLoadingIntent, clientSecret]);

    // On open, fetch methods
    useEffect(() => {
        if (open && course) {
            fetchMethods();
            setClientSecret(null);
            setError('');
        }
    }, [open, course, fetchMethods]);

    // Create payment intent when switching to new card tab or no saved cards
    useEffect(() => {
        if (open && !isLoadingMethods && (activeTab === 'new' || methods.length === 0) && !clientSecret) {
            createPaymentIntent();
        }
    }, [open, activeTab, isLoadingMethods, methods.length, clientSecret, createPaymentIntent]);

    // Purchase with saved card
    const handlePurchaseWithSavedCard = async () => {
        if (!course || !selectedMethodId) return;
        setIsProcessing(true);
        setError('');
        try {
            const selectedMethod = methods.find((m) => m.id === selectedMethodId);
            const res = await axiosInstance.post('/api/lms/portal/courses/server-side-purchase', {
                course_id: course.course_identity || course.id,
                payment_method_db_id: selectedMethodId,
                gateway_payment_method_id: selectedMethod?.gateway_id,
                amount_paid: effectivePrice,
                currency: course.currency || 'usd',
                payment_method_details: {
                    card_brand: selectedMethod?.card_brand,
                    last4: selectedMethod?.last4,
                },
            });
            if (res.data?.success) {
                onPurchaseSuccess?.(course.course_identity || course.id);
                onClose();
            } else {
                setError(res.data?.message || 'Purchase failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Purchase failed. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    // After new card payment succeeds, verify purchase
    const handleNewCardSuccess = async (paymentIntentId) => {
        setIsProcessing(true);
        setError('');
        try {
            const res = await axiosInstance.post('/api/lms/portal/courses/verify-purchase', {
                payment_intent_id: paymentIntentId,
            });
            if (res.data?.success) {
                onPurchaseSuccess?.(course.course_identity || course.id);
                onClose();
            } else {
                setError(res.data?.message || 'Failed to verify purchase');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to verify purchase');
        } finally {
            setIsProcessing(false);
        }
    };

    if (!course) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', maxHeight: '90vh' } }}>
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: 480 }}>
                    {/* Left: Course Info */}
                    <Box sx={{ flex: '0 0 340px', bgcolor: '#1e293b', color: 'white', p: 3, display: { xs: 'none', md: 'flex' }, flexDirection: 'column' }}>
                        {/* Thumbnail */}
                        {course.thumbnail_url ? (
                            <Box component="img" src={course.thumbnail_url} alt={course.title}
                                sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: '12px', mb: 2 }} />
                        ) : (
                            <Box sx={{ width: '100%', height: 160, background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`, borderRadius: '12px', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SchoolIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.6)' }} />
                            </Box>
                        )}

                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', mb: 1, lineHeight: 1.3 }}>{course.title}</Typography>

                        {/* Stats */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                            {course.modules?.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LayersIcon sx={{ fontSize: 14, color: ACCENT_LIGHT }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>{course.modules.length} modules</Typography>
                                </Box>
                            )}
                            {totalLessons > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PlayCircleFilledIcon sx={{ fontSize: 14, color: ACCENT_LIGHT }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>{totalLessons} lessons</Typography>
                                </Box>
                            )}
                            {course.estimated_duration_minutes > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: 14, color: ACCENT_LIGHT }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>{Math.round(course.estimated_duration_minutes / 60)}h</Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Price */}
                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '12px', p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: ACCENT_LIGHT }}>
                                    {getCurrencySymbol(course.currency)}{Number(effectivePrice).toFixed(2)}
                                </Typography>
                                {course.is_discount_active && course.discount_percentage > 0 && (
                                    <>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b', textDecoration: 'line-through' }}>
                                            {getCurrencySymbol(course.currency)}{Number(course.price).toFixed(2)}
                                        </Typography>
                                        <Chip label={`-${course.discount_percentage}%`} size="small" sx={{ fontFamily, fontWeight: 700, fontSize: '0.65rem', bgcolor: '#ef4444', color: 'white', height: 20 }} />
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* What you get */}
                        <Box sx={{ mt: 'auto' }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>What you'll get</Typography>
                            {[
                                'Lifetime access',
                                course.is_certificate_enabled && 'Certificate of completion',
                                'Access on all devices',
                            ].filter(Boolean).map((item, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                                    <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#cbd5e1' }}>{item}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Right: Payment */}
                    <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>Complete Purchase</Typography>
                            <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8' }}><CloseIcon fontSize="small" /></IconButton>
                        </Box>

                        {/* Mobile price */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: '12px' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', flex: 1 }}>{course.title}</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, color: ACCENT, fontSize: '1.1rem' }}>{getCurrencySymbol(course.currency)}{Number(effectivePrice).toFixed(2)}</Typography>
                        </Box>

                        {/* Error */}
                        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: '10px', fontFamily, fontSize: '0.85rem' }}>{error}</Alert>}

                        {/* Tabs */}
                        {methods.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                                {['saved', 'new'].map((tab) => (
                                    <Button key={tab} onClick={() => setActiveTab(tab)} variant={activeTab === tab ? 'contained' : 'outlined'}
                                        sx={{
                                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', fontSize: '0.8rem', flex: 1, py: 1,
                                            ...(activeTab === tab
                                                ? { bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, boxShadow: 'none' }
                                                : { borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }),
                                        }}>
                                        {tab === 'saved' ? 'Saved Cards' : 'New Card'}
                                    </Button>
                                ))}
                            </Box>
                        )}

                        {/* Loading */}
                        {isLoadingMethods ? (
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CircularProgress size={32} sx={{ color: ACCENT }} />
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Saved Cards Tab */}
                                {activeTab === 'saved' && methods.length > 0 && (
                                    <>
                                        <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                                            {methods.map((method) => (
                                                <Box key={method.id} onClick={() => setSelectedMethodId(method.id)}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1, borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s',
                                                        border: selectedMethodId === method.id ? `2px solid ${ACCENT}` : '2px solid #e2e8f0',
                                                        bgcolor: selectedMethodId === method.id ? `${ACCENT}08` : 'white',
                                                        '&:hover': { borderColor: selectedMethodId === method.id ? ACCENT : '#cbd5e1' },
                                                    }}>
                                                    <Radio checked={selectedMethodId === method.id} size="small" sx={{ p: 0, color: '#cbd5e1', '&.Mui-checked': { color: ACCENT } }} />
                                                    <CreditCardIcon sx={{ fontSize: 20, color: '#64748b' }} />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', textTransform: 'capitalize' }}>
                                                            {method.card_brand} •••• {method.last4}
                                                        </Typography>
                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                                            Expires {String(method.exp_month).padStart(2, '0')}/{method.exp_year}
                                                        </Typography>
                                                    </Box>
                                                    {method.is_default && (
                                                        <Chip label="Default" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.6rem', bgcolor: `${ACCENT}15`, color: ACCENT, height: 20 }} />
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                        <Button fullWidth variant="contained" onClick={handlePurchaseWithSavedCard}
                                            disabled={isProcessing || !selectedMethodId}
                                            sx={{ fontFamily, fontWeight: 700, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '12px', py: 1.5, fontSize: '0.9rem', boxShadow: `0 4px 16px ${ACCENT}44` }}>
                                            {isProcessing ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} sx={{ color: 'white' }} /> Processing...</Box>
                                            ) : (
                                                `Pay ${getCurrencySymbol(course.currency)}${Number(effectivePrice).toFixed(2)}`
                                            )}
                                        </Button>
                                    </>
                                )}

                                {/* New Card Tab */}
                                {(activeTab === 'new' || methods.length === 0) && (
                                    <>
                                        {isLoadingIntent ? (
                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                                                <CircularProgress size={28} sx={{ color: ACCENT }} />
                                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Preparing payment...</Typography>
                                            </Box>
                                        ) : clientSecret ? (
                                            <Elements stripe={stripePromise} options={{ clientSecret }}>
                                                <NewCardForm course={course} clientSecret={clientSecret} onSuccess={handleNewCardSuccess}
                                                    onError={setError} isProcessingExternal={isProcessing} />
                                            </Elements>
                                        ) : (
                                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>Unable to initialize payment. Please try again.</Typography>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        )}

                        {/* Security badge */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                            <ShieldIcon sx={{ fontSize: 14, color: '#10b981' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>Secure payment encrypted by Stripe</Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
